import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaProjectDiagram, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaUsers, 
  FaMoneyBillWave,
  FaSave,
  FaArrowLeft,
  FaExclamationTriangle
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

// Styled Components
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      text-decoration: none;
      
      &:hover {
        color: #F68B21;
        background: rgba(246, 139, 33, 0.1);
      }
    }
  }
  
  h1 {
    font-size: 2rem;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: #141340;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-weight: 600;
    color: #141340;
    font-size: 0.9rem;
  }
  
  input, select, textarea {
    padding: 0.75rem;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #F68B21;
      box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
    }
    
    &:disabled {
      background: #f8f9fa;
      color: #6c757d;
      cursor: not-allowed;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
  
  .error {
    color: #dc3545;
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }
`;

const BudgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const BudgetSummary = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #F68B21;
  
  .total-label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
  }
  
  .total-amount {
    font-size: 1.2rem;
    font-weight: 700;
    color: #F68B21;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  color: #6c757d;
  border: 1px solid #e9ecef;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #F68B21;
    border-color: #F68B21;
    background: rgba(246, 139, 33, 0.1);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #F68B21;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }
  
  h3 {
    margin-bottom: 0.5rem;
  }
`;

interface ProjectFormData {
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string;
  status: string;
  teamSize: number;
  budget: {
    materials: number;
    labor: number;
    equipment: number;
    subContracts: number;
    others: number;
  };
}

const ManagerProjectForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    location: '',
    startDate: '',
    endDate: '',
    description: '',
    status: 'Draft',
    teamSize: 0,
    budget: {
      materials: 0,
      labor: 0,
      equipment: 0,
      subContracts: 0,
      others: 0
    }
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  useEffect(() => {
    const fetchProject = async () => {
      if (id) {
        setIsEdit(true);
        try {
          setLoading(true);
          const projectResponse = await apiService.getProject(id);
          const project = projectResponse.data;
          
          if (project) {
            // Check if user has access to this project
            const isDirectManager = project.managerId === user?.id || project.managerId?._id === user?.id;
            const isAssignedManager = project.assignedStaff?.some((staff: any) => 
              (staff.userId === user?.id || staff.userId?._id === user?.id) && staff.role === 'Project Manager'
            );
            
            if (isDirectManager || isAssignedManager) {
              setFormData({
                name: project.name,
                location: project.location,
                startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
                endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
                description: project.description || '',
                status: project.status,
                teamSize: project.assignedStaff?.length || 0,
                budget: project.budget || {
                  materials: 0,
                  labor: 0,
                  equipment: 0,
                  subContracts: 0,
                  others: 0,
                  total: 0
                }
              });
            } else {
              setError('You do not have access to edit this project');
            }
          } else {
            setError('Project not found');
          }
        } catch (error) {
          console.error('Error fetching project:', error);
          setError('Failed to load project');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id, user]);
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    
    if (formData.startDate && formData.endDate && new globalThis.Date(formData.startDate) >= new globalThis.Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    
    if (formData.teamSize < 0) {
      newErrors.teamSize = 'Team size must be a positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof ProjectFormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  const calculateTotalBudget = () => {
    return Object.values(formData.budget).reduce((sum, value) => sum + (value || 0), 0);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real application, you would save the data to the backend
      console.log('Saving project:', formData);
      
      // Navigate back to projects list
      navigate('/manager/projects');
    } catch (error) {
      setError('Failed to save project. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>Loading project data...</LoadingSpinner>
      </PageContainer>
    );
  }
  
  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>
          <IconWrapper icon={FaExclamationTriangle} className="icon" />
          <h3>Error</h3>
          <p>{error}</p>
        </ErrorMessage>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/manager/projects')}>
            <IconWrapper icon={FaArrowLeft} />
            Back to Projects
          </button>
          <h1>
            <IconWrapper icon={FaProjectDiagram} className="icon" />
            {isEdit ? 'Edit Project' : 'New Project'}
          </h1>
        </div>
      </PageHeader>
      
      <FormContainer>
        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>
              <IconWrapper icon={FaProjectDiagram} className="icon" />
              Basic Information
            </h3>
            <FormGrid>
              <FormGroup>
                <label>Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter project name"
                />
                {errors.name && <div className="error">{errors.name}</div>}
              </FormGroup>
              
              <FormGroup>
                <label>Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Enter project location"
                />
                {errors.location && <div className="error">{errors.location}</div>}
              </FormGroup>
              
              <FormGroup>
                <label>Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                />
                {errors.startDate && <div className="error">{errors.startDate}</div>}
              </FormGroup>
              
              <FormGroup>
                <label>End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                />
                {errors.endDate && <div className="error">{errors.endDate}</div>}
              </FormGroup>
              
              <FormGroup>
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="Draft">Draft</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </FormGroup>
              
              <FormGroup>
                <label>Team Size</label>
                <input
                  type="number"
                  value={formData.teamSize}
                  onChange={(e) => handleInputChange('teamSize', parseInt(e.target.value) || 0)}
                  min="0"
                />
                {errors.teamSize && <div className="error">{errors.teamSize}</div>}
              </FormGroup>
            </FormGrid>
            
            <FormGroup>
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter project description"
              />
            </FormGroup>
          </FormSection>
          
          <FormSection>
            <h3>
              <IconWrapper icon={FaMoneyBillWave} className="icon" />
              Budget Breakdown
            </h3>
            <BudgetGrid>
              <FormGroup>
                <label>Materials (TZS)</label>
                <input
                  type="number"
                  value={formData.budget.materials}
                  onChange={(e) => handleInputChange('budget.materials', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </FormGroup>
              
              <FormGroup>
                <label>Labor (TZS)</label>
                <input
                  type="number"
                  value={formData.budget.labor}
                  onChange={(e) => handleInputChange('budget.labor', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </FormGroup>
              
              <FormGroup>
                <label>Equipment (TZS)</label>
                <input
                  type="number"
                  value={formData.budget.equipment}
                  onChange={(e) => handleInputChange('budget.equipment', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </FormGroup>
              
              <FormGroup>
                <label>Sub-contracts (TZS)</label>
                <input
                  type="number"
                  value={formData.budget.subContracts}
                  onChange={(e) => handleInputChange('budget.subContracts', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </FormGroup>
              
              <FormGroup>
                <label>Others (TZS)</label>
                <input
                  type="number"
                  value={formData.budget.others}
                  onChange={(e) => handleInputChange('budget.others', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </FormGroup>
            </BudgetGrid>
            
            <BudgetSummary>
              <div className="total-label">Total Budget</div>
              <div className="total-amount">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'TZS',
                  maximumFractionDigits: 0
                }).format(calculateTotalBudget())}
              </div>
            </BudgetSummary>
          </FormSection>
          
          <FormActions>
            <CancelButton type="button" onClick={() => navigate('/manager/projects')}>
              Cancel
            </CancelButton>
            <SaveButton type="submit" disabled={saving}>
              <IconWrapper icon={FaSave} />
              {saving ? 'Saving...' : (isEdit ? 'Update Project' : 'Create Project')}
            </SaveButton>
          </FormActions>
        </form>
      </FormContainer>
    </PageContainer>
  );
};

export default ManagerProjectForm; 